<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import { usePipelinesStore } from 'dashboard/stores/pipelines';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import DealWonModal from 'dashboard/components-next/Deals/DealWonModal.vue';
import DealLostModal from 'dashboard/components-next/Deals/DealLostModal.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  contactId: { type: Number, required: true },
});

const { t } = useI18n();
const dealsStore = useDealsStore();
const pipelinesStore = usePipelinesStore();

const deals = ref([]);
const isOpen = ref(false);
const wonModalRef = ref(null);
const lostModalRef = ref(null);

const stagesFor = deal => {
  const pipeline = pipelinesStore.getPipelinesList.find(p =>
    (p.stages || []).some(s => s.id === deal.pipelineStageId)
  );
  return pipeline?.stages || [];
};

const menuSections = computed(() =>
  deals.value.map(deal => ({
    title: deals.value.length > 1 ? deal.name : '',
    items: stagesFor(deal).map(stage => ({
      label: stage.name,
      action: 'move',
      value: stage.id,
      dealId: deal.id,
      icon:
        stage.id === deal.pipelineStageId
          ? 'i-lucide-check'
          : 'i-lucide-circle',
    })),
  }))
);

const loadDeals = async () => {
  const contactDeals = await dealsStore.getByContact(props.contactId);
  deals.value = contactDeals.filter(
    d => d.conversationDisplayId === props.conversationId
  );
};

const moveDeal = async ({ dealId, stageId, value, lossReasonId }) => {
  const attrs = { id: dealId, pipeline_stage_id: stageId };
  if (value !== undefined) attrs.value = value;
  if (lossReasonId !== undefined) attrs.loss_reason_id = lossReasonId;
  try {
    const updated = await dealsStore.update(attrs);
    deals.value = deals.value.map(d => (d.id === dealId ? updated : d));
  } catch {
    useAlert(t('DEALS.KANBAN.MOVE_ERROR'));
  }
};

const onSelectStage = ({ value: stageId, dealId }) => {
  isOpen.value = false;
  const deal = deals.value.find(d => d.id === dealId);
  if (stageId === deal.pipelineStageId) return;
  const { stageType } = stagesFor(deal).find(s => s.id === stageId);
  const context = { dealId, stageId, deal };
  if (stageType === 'won') wonModalRef.value.open(context);
  else if (stageType === 'lost') lostModalRef.value.open(context);
  else moveDeal(context);
};

watch(() => [props.conversationId, props.contactId], loadDeals);
onMounted(() => {
  pipelinesStore.get();
  loadDeals();
});
</script>

<template>
  <div
    v-if="deals.length"
    v-on-clickaway="() => (isOpen = false)"
    class="relative"
  >
    <Button
      v-tooltip.top-end="t('DEALS.KANBAN.CHANGE_STAGE')"
      icon="i-lucide-kanban"
      slate
      faded
      sm
      @click="isOpen = !isOpen"
    />
    <DropdownMenu
      v-if="isOpen"
      :menu-sections="menuSections"
      class="ltr:left-0 rtl:right-0 mt-1 w-52 top-full"
      @action="onSelectStage"
    />
    <DealWonModal ref="wonModalRef" @confirm="moveDeal" />
    <DealLostModal ref="lostModalRef" @confirm="moveDeal" />
  </div>
</template>
